import React, { useState, useEffect, useRef } from "react";
import icons from "./data/icons";
import api from "./api/api";
import EmailLink from "./EmailLink";
import PhoneLink from "./phonelink";
import { Turnstile } from "@marsidev/react-turnstile";
import { verifyTurnstile } from "../components/api/verify-turnstile";
const Contact = () => {
  const [state, setState] = useState({ succeeded: false, message: "" });
  const [formValues, setFormValues] = useState({ name: "", email: "", message: "" });
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileVerified, setTurnstileVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);

  // Icon map for social links
  const iconMap = {
    linkedin: icons.Linkedin,
    github: icons.Github,
    linktree: icons.SiLinktree,
    instagram: icons.Instagram,
    facebook: icons.Facebook,
    x: icons.FaXTwitter,
    threads: icons.FaThreads,
    youtube: icons.Youtube,
  };
  const handleTurnstileVerify = async (token) => {
    if (!token) {
      setTurnstileToken("");
      setTurnstileVerified(false);
      return;
    }

    setTurnstileToken(token);
    setVerifying(true);

    try {
      const response = await verifyTurnstile(token);

      if (response.data?.success === true) {
        setTurnstileVerified(true);
      } else {
        setTurnstileToken("");
        setTurnstileVerified(false);
      }
    } catch (error) {
      console.error(
        "Turnstile verification failed:",
        error.response?.data || error.message
      );

      setTurnstileToken("");
      setTurnstileVerified(false);
    } finally {
      setVerifying(false);
    }
  };
  // Fetch contact data
  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await api.get("/contact");
        console.log("Contact API response:", response.data);

        if (response.data.success && response.data.data.length > 0) {
          setData(response.data.data[0]);
        } else {
          console.error("No contact data found");
        }
      } catch (error) {
        console.error("Failed to fetch contact data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, []);

  // Intersection observer for animation
  useEffect(() => {
    const checkMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsMobile(checkMobile);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (
      !formValues.name.trim() ||
      !formValues.email.trim() ||
      !formValues.message.trim()
    ) {
      setState({
        succeeded: false,
        message: data.fillDetailsMessage,
      });

      return;
    }

    setState({
      succeeded: false,
      message: data.sendingButtonText || "Sending...",
    });

    const formData = new FormData();

    formData.append(
      "access_key",
      data.web3formsAccessKey
    );

    formData.append("name", formValues.name.trim());
    formData.append("email", formValues.email.trim());
    formData.append("message", formValues.message.trim());

    try {
      const response = await fetch(
        "https://api.web3forms.com/submit",
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        setState({
          succeeded: true,
          message: "",
        });

        setFormValues({
          name: "",
          email: "",
          message: "",
        });
      } else {
        setState({
          succeeded: false,
          message:
            result.message ||
            data.failMessage ||
            "Submission failed. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Web3Forms error:", error);

      setState({
        succeeded: false,
        message:
          data.failMessage ||
          "Submission failed. Please try again later.",
      });
    }
  };

  // ---- Loading state ----
  if (loading) {
    return (
      <div id="contact" ref={sectionRef} className="relative bg-[#0C121D] py-16 sm:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-5xl mx-auto flex items-center justify-center h-64">
          <div className="text-white text-lg">Loading contact information…</div>
        </div>
      </div>
    );
  }

  // ---- No data fallback ----
  if (!data) {
    return (
      <div id="contact" ref={sectionRef} className="relative bg-[#0C121D] py-16 sm:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-indigo-500/30 blur-xl"></div>
            <div className="relative h-14 w-14 animate-spin rounded-full border-[6px] border-slate-700 border-t-indigo-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Main render (data is guaranteed to exist) ----
  return (
    <div id="contact" ref={sectionRef} className="relative bg-[#0C121D] py-16 sm:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div
        className={`max-w-5xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
      >
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 leading-tight sm:leading-tight px-4 sm:px-0">
            {data.contactTitle}
          </h2>
          <p className="text-slate-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed px-4 sm:px-0">
            {data.contactSubtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Left Side - Contact Info */}
          <div className="space-y-6 h-full">
            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-xl sm:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 h-full">
              <div className="flex items-center gap-2 mb-8">
                <icons.Sparkles className="w-5 h-5 text-cyan-400" />
                <h3 className="text-2xl font-bold text-white">{data.getInTouchTitle}</h3>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 mb-8 group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 flex items-center justify-center group-hover:scale-110 group-active:scale-105 transition-transform duration-300 shrink-0">
                  <icons.Mail className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                </div>

                <div className="flex-1 relative min-w-0">
                  <p className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">Email</p>
                  <p className="text-white text-xs sm:text-sm mb-2 sm:mb-3 leading-relaxed">
                    {data.emailDescription}
                  </p>
                  <EmailLink
                    email={data.email}
                    className="text-cyan-400 text-xs sm:text-sm font-medium hover:text-cyan-300 active:text-cyan-200 transition-colors text-left touch-manipulation inline-block"
                  >
                    {data.sendEmailText}
                  </EmailLink>
                </div>
              </div>
              {data.showPhone && (
                <div className="flex items-start gap-4 mb-8 group">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 flex items-center justify-center group-hover:scale-110 group-active:scale-105 transition-transform duration-300 shrink-0">
                    <icons.Phone className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                  </div>

                  <div className="flex-1 relative min-w-0">
                    <p className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">
                      Phone
                    </p>

                    <p className="text-white text-xs sm:text-sm mb-2 sm:mb-3 leading-relaxed">
                      {data.phoneDescription}
                    </p>

                    <PhoneLink
                      phone={data.phone}
                      className="text-cyan-400 text-xs sm:text-sm font-medium hover:text-cyan-300 active:text-cyan-200 transition-colors text-left touch-manipulation inline-block"
                    >
                      {data.phoneText}
                    </PhoneLink>
                  </div>
                </div>
              )}
              {/* Location */}
              {data.showLocation && (
                <div className="flex items-start gap-4 mb-8 group">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:scale-110 group-active:scale-105 transition-transform duration-300 shrink-0">
                    <icons.MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">Location</p>
                    <p className="text-white text-xs sm:text-sm lg:text-base truncate">
                      {data.location}
                    </p>
                  </div>
                </div>
              )}

              {/* Social Links */}
              <div className="space-y-8">
                {data.socialLinks
                  ?.filter((link) => link.show)
                  .map((link) => {
                    const Icon = icons[link.icon];

                    return (
                      <div
                        key={link.id}
                        className="flex items-start gap-4 group"
                      >
                        {/* Icon Box */}
                        <div
                          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl border flex items-center justify-center group-hover:scale-110 group-active:scale-105 transition-transform duration-300 shrink-0 ${link.boxClass || ""
                            }`}
                        >
                          {Icon && (
                            <Icon
                              className="w-5 h-5 sm:w-6 sm:h-6"
                              style={{
                                color: link.iconColor || "#60A5FA",
                              }}
                            />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-400 text-xs sm:text-sm mb-0.5 sm:mb-1">
                            {link.title || link.label || "Untitled"}
                          </p>

                          <p className="text-white text-xs sm:text-sm mb-2 sm:mb-3 leading-relaxed">
                            {link.description || ""}
                          </p>

                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs sm:text-sm font-medium transition-colors active:opacity-80 touch-manipulation inline-block"
                            style={{
                              color: link.actionColor || "#22D3EE",
                            }}
                          >
                            {link.actionText || "Visit"}
                          </a>
                        </div>
                      </div>
                    );
                  })}
              </div>

            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-4 sm:mb-6 lg:mb-8 px-4 sm:px-0">
              <icons.Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 shrink-0" />
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white leading-tight">
                {data.sendMessageTitle}
              </h3>
            </div>

            {state.succeeded ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-white mb-2">{data.successTitle}</h4>
                <p className="text-slate-400">{data.successMessage}</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-slate-300 text-sm font-medium mb-2">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={formValues.name}
                    onChange={onChange}
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-slate-300 text-sm font-medium mb-2">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="your.email@example.com"
                    value={formValues.email}
                    onChange={onChange}
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-slate-300 text-sm font-medium mb-2">
                    Message <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Hi Vinayak..."
                    rows="5"
                    value={formValues.message}
                    onChange={onChange}
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all resize-none"
                  />
                </div>
                <div className="w-full flex flex-col items-center justify-center">

                  <Turnstile
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                    onSuccess={handleTurnstileVerify}
                    onExpire={() => {
                      setTurnstileToken("");
                      setTurnstileVerified(false);
                    }}
                    onError={() => {
                      setTurnstileToken("");
                      setTurnstileVerified(false);
                      setVerifying(false);
                    }}
                  />
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={state.message === data.sendingButtonText}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 active:from-blue-700 active:to-cyan-600 text-white font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-cyan-500/50 active:shadow-cyan-500/70 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 group disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 active:scale-95 touch-manipulation text-sm sm:text-base"
                >
                  <icons.Send className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 group-active:translate-x-1 transition-transform shrink-0" />
                  <span>
                    {state.message === data.sendingButtonText
                      ? data.sendingButtonText
                      : data.sendButtonText}
                  </span>
                </button>

                {state.message && state.message !== data.sendingButtonText && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">
                    {state.message}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;